import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api, { examService } from '../services/api';
import { useAuth } from './AuthContext';

const ExamContext = createContext();

// Normalize answers: keep 0 for "seen but not answered", convert '0' string to 0
// Remove only null/undefined values
const sanitizeAnswers = (answers) => {
    if (!answers || typeof answers !== 'object') return {};
    const clean = {};
    for (const [qId, ans] of Object.entries(answers)) {
        if (ans === null || ans === undefined) continue;
        // Normalize '0' string to number 0
        clean[qId] = ans === '0' ? 0 : ans;
    }
    return clean;
};

export const ExamProvider = ({ children }) => {
    // Current active session state
    const [examId, setExamId] = useState(null);
    const [sessionId, setSessionId] = useState(null); // Real DB session ID (idSession)
    const [moduleId, setModuleId] = useState(null);
    const [category, setCategory] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [onDemandAnswers, setOnDemandAnswers] = useState({}); // { questionId: { correctOptionIndex, explanation } }
    const [answers, setAnswers] = useState({}); // { questionIndex: selectedOptionIndex }
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [mode, setMode] = useState('classic'); // 'classic' | 'flash'
    const [isPaused, setIsPaused] = useState(false);
    const [isFinished, setIsFinished] = useState(false);
    const [timeInSeconds, setTimeInSeconds] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [minScore, setMinScore] = useState(75);
    const [minSubScore, setMinSubScore] = useState(0);
    const [matiereResults, setMatiereResults] = useState(null);
    const [hasAnnexe, setHasAnnexe] = useState(false);
    const [historicalScore, setHistoricalScore] = useState(null); // Score from DB for review sessions
    const [historicalDate, setHistoricalDate] = useState(null); // Date from DB for review sessions
    const [isKeywordExam, setIsKeywordExam] = useState(false); // Virtual keyword-based exams (no stats)
    const [customExamMeta, setCustomExamMeta] = useState(null); // { examType, config, questionIds, levelId, pageIndex, dateStart }
    const [annexeExamId, setAnnexeExamId] = useState(null); // For custom exams: the source exam ID for annexe download
    const [isVuExamen, setIsVuExamen] = useState(false); // True for "Vu à l'examen" exams (queries vu_examen_* tables)
    const [examTitle, setExamTitle] = useState(''); // Display title shown in ExamHeader (set by callers via setExamTitle)

    const timerRef = useRef(null);

    // Refs to capture latest values without triggering re-renders
    const latestValuesRef = useRef({});

    // Update refs whenever values change
    useEffect(() => {
        latestValuesRef.current = {
            examId,
            sessionId,
            moduleId,
            category,
            answers,
            currentQuestionIndex,
            mode,
            timeInSeconds,
            isFinished,
            isVuExamen
        };
    }, [examId, sessionId, moduleId, category, answers, currentQuestionIndex, mode, timeInSeconds, isFinished, isVuExamen]);

    // Save session to backend - called on significant events only
    // Uses refs to get current timer value without creating dependency
    const persistSession = useCallback(async () => {
        const current = latestValuesRef.current;
        if (!current.examId || current.isFinished) return;

        const result = await examService.saveExamSession(current.examId, {
            moduleId: current.moduleId,
            category: current.category,
            answers: current.answers,
            currentQuestionIndex: current.currentQuestionIndex,
            mode: current.mode,
            timeInSeconds: current.timeInSeconds, // Captures current timer value
            examType: current.isVuExamen ? 'vu' : 'standard'
        });

        // Capture sessionId from first INSERT (new exam)
        if (result?.sessionId && !current.sessionId) {
            setSessionId(result.sessionId);
        }

        return result;
    }, []); // No dependencies - stable function that reads from ref

    // Timer effect
    useEffect(() => {
        if (examId && !isPaused && !isFinished) {
            timerRef.current = setInterval(() => {
                setTimeInSeconds(prev => prev + 1);
            }, 1000);
        } else {
            clearInterval(timerRef.current);
        }
        return () => clearInterval(timerRef.current);
    }, [examId, isPaused, isFinished]);

    // Autosave effect - triggers only on significant events (answer, question change, pause)
    // NOT on timer changes to reduce backend calls
    // Keyword exams: skip autosave entirely (no backend session)
    useEffect(() => {
        if (isKeywordExam) return;
        const debouncedSave = setTimeout(persistSession, 1000);
        return () => clearTimeout(debouncedSave);
    }, [answers, currentQuestionIndex, isPaused, persistSession, isKeywordExam]);

    const { user } = useAuth();

    const startExam = async (mId, cat, initialMode = 'classic', existingExamId = null, { passingScore = 75, passingSubScore = 0, annexe = false, preloadedQuestions = null, keywordExam = false, customExamMeta: metaArg = null, vuExamen = false } = {}) => {
        // Pre-flight: verify session is still valid (detects another device login)
        try { await api.checkSession(); } catch { return; }
        setIsLoading(true);
        setMinScore(passingScore);
        setMinSubScore(passingSubScore);
        // For custom exams with annexeExamId, set hasAnnexe from meta
        const customAnnexe = keywordExam && metaArg?.annexeExamId ? true : false;
        setHasAnnexe(annexe || customAnnexe);
        setAnnexeExamId(keywordExam && metaArg?.annexeExamId ? metaArg.annexeExamId : null);
        setHistoricalScore(null);
        setHistoricalDate(new Date().toISOString());
        setIsKeywordExam(keywordExam);
        setIsVuExamen(vuExamen);
        setCustomExamMeta(keywordExam && metaArg ? { ...metaArg, dateStart: new Date().toISOString() } : null);
        const id = existingExamId || `${mId}-${cat}-${Date.now()}`;
        const examTypeParam = vuExamen ? 'vu' : 'standard';

        // Keyword exams: no saved session to restore
        const savedSession = keywordExam ? null : await examService.getExamSession(id);

        // Use preloaded questions (keyword exams) or fetch from backend
        const fetchedQuestions = preloadedQuestions || await examService.getQuestionsByExam(mId, cat, id, user?.id, { type: examTypeParam });

        // Shuffle options for frontend display
        const processedQuestions = fetchedQuestions.map(q => ({
            ...q,
            options: q.options ? [...q.options].sort(() => Math.random() - 0.5) : []
        }));

        setQuestions(processedQuestions);
        setOnDemandAnswers({});
        setMatiereResults(null);

        if (savedSession) {
            setExamId(id);
            setSessionId(savedSession.sessionId); // Store real DB session ID
            setModuleId(savedSession.moduleId || mId);
            setCategory(savedSession.category || cat);
            const cleanAnswers = sanitizeAnswers(savedSession.answers);
            setAnswers(cleanAnswers); // Now stores { questionId: answerId }

            // In Flash mode, if current question is answered, move to next unanswered
            // Clamp to valid range (questions may have changed since session was saved)
            let resumeIndex = Math.min(savedSession.currentQuestionIndex || 0, processedQuestions.length - 1);
            const sessionMode = savedSession.mode || initialMode;
            if (sessionMode === 'flash' && cleanAnswers) {
                const currentQuestionId = processedQuestions[resumeIndex]?.id;
                if (currentQuestionId && cleanAnswers[currentQuestionId]) {
                    // Current question already answered, move to next
                    resumeIndex = Math.min(resumeIndex + 1, processedQuestions.length - 1);
                }
            }

            setCurrentQuestionIndex(resumeIndex);
            setMode(sessionMode);
            setTimeInSeconds(savedSession.timeInSeconds || 0);

            // Flash mode: preload corrections for previously answered questions
            const answeredCount = Object.values(cleanAnswers).filter(a => a !== 0 && a !== '0' && a != null).length;
            if (sessionMode === 'flash' && answeredCount > 0) {
                try {
                    const data = keywordExam
                        ? await api.getKeywordExamAnswers(processedQuestions.map(q => q.id))
                        : await examService.getExamAnswers(id, user?.id, { type: examTypeParam });
                    if (data) setOnDemandAnswers(data);
                } catch (e) {
                    console.error('Error preloading flash corrections:', e);
                }
            }
        } else {
            setExamId(id);
            setSessionId(null); // Reset — will be set by first persistSession INSERT
            setModuleId(mId);
            setCategory(cat);
            setAnswers({});
            setCurrentQuestionIndex(0);
            setMode(initialMode);
            setTimeInSeconds(0);
        }

        setIsPaused(false);
        setIsFinished(false);
        setIsLoading(false);
    };

    const loadAnswers = async (answersOnly = true, examIdOverride = null, examTypeOverride = null) => {
        const targetId = examIdOverride || examId;
        if (!targetId) return;
        try {
            let data;
            if (isKeywordExam) {
                // Keyword exams: fetch answers by question IDs via keyword-exams endpoint
                const qIds = questions.map(q => q.id);
                data = await api.getKeywordExamAnswers(qIds);
            } else {
                const type = examTypeOverride || (isVuExamen ? 'vu' : 'standard');
                data = await examService.getExamAnswers(targetId, user?.id, { type });
            }
            setOnDemandAnswers(prev => ({ ...prev, ...data }));
            return data;
        } catch (error) {
            console.error("Error loading answers:", error);
        }
    };

    const pauseExam = () => setIsPaused(true);
    const resumeExam = () => setIsPaused(false);

    const stopExam = async () => {
        if (!isKeywordExam) {
            await persistSession();
        }
        setExamId(null);
    };

    const restartExam = (newMode) => {
        latestValuesRef.current.isFinished = false; // Sync reset so autosave works again
        setAnswers({}); // Clear answers
        setOnDemandAnswers({});
        setCurrentQuestionIndex(0);
        setTimeInSeconds(0);
        setIsFinished(false);
        setMatiereResults(null);
        setMode(newMode);
        if (!isKeywordExam) {
            persistSession();
        }
    };

    const submitAnswer = (questionId, answerId) => {
        if (isFinished) return;
        setAnswers(prev => ({ ...prev, [questionId]: answerId }));
    };

    // Mark a question as "seen but not answered" (value 0)
    // Called when user navigates away from a question without answering
    const markQuestionSeen = (questionId) => {
        if (isFinished) return;
        setAnswers(prev => {
            // Only mark if not already answered (real answer or 0)
            if (prev[questionId] === undefined) {
                return { ...prev, [questionId]: 0 };
            }
            return prev;
        });
    };

    const finishExam = async () => {
        // Synchronously flag as finished in the ref BEFORE any async work.
        // This prevents a pending autosave timeout from re-creating the session
        // after history.php deletes it (race condition with the 1s debounce).
        latestValuesRef.current.isFinished = true;

        // Fetch all answers if not already loaded
        const allAnswers = await loadAnswers();

        setIsFinished(true);

        // Calculate final score
        let correctCount = 0;
        questions.forEach((q) => {
            const secret = allAnswers?.[q.id] || onDemandAnswers[q.id];
            // Compare User Answer ID with Correct Answer ID (string cast for legacy type mismatch)
            if (answers[q.id] !== undefined && secret && String(answers[q.id]) === String(secret.correctReponseId)) {
                correctCount++;
            }
        });

        const score = questions.length > 0 ? Math.round((correctCount / questions.length) * 100) : 0;

        // Per-matière breakdown for full/complet exams
        if (category === 'complet' || category === 'full') {
            const matiereMap = {};
            questions.forEach((q) => {
                if (!q.matiere) return;
                const mId = q.matiere.id;
                if (!matiereMap[mId]) {
                    matiereMap[mId] = { id: mId, code: q.matiere.code, name: q.matiere.name, total: 0, correct: 0 };
                }
                matiereMap[mId].total++;
                const secret = allAnswers?.[q.id] || onDemandAnswers[q.id];
                if (answers[q.id] !== undefined && secret && String(answers[q.id]) === String(secret.correctReponseId)) {
                    matiereMap[mId].correct++;
                }
            });

            const computedMatiereResults = Object.values(matiereMap).map(m => ({
                ...m,
                score: m.total > 0 ? Math.round((m.correct / m.total) * 100) : 0,
                passed: m.total > 0 ? Math.round((m.correct / m.total) * 100) >= minSubScore : true
            }));
            setMatiereResults(computedMatiereResults);
        } else {
            setMatiereResults(null);
        }

        // Keyword exams: save to custom history table (separate from regular stats)
        if (isKeywordExam) {
            if (customExamMeta) {
                try {
                    await api.saveCustomExamHistory({
                        levelId: customExamMeta.levelId,
                        examType: customExamMeta.examType,
                        config: customExamMeta.config,
                        questionIds: customExamMeta.questionIds,
                        dateStart: customExamMeta.dateStart,
                        score,
                        duration: timeInSeconds,
                        answers,
                        mode,
                        pageIndex: customExamMeta.pageIndex ?? 0
                    });
                } catch (e) {
                    console.error('Error saving custom exam history:', e);
                }
            }
            return score;
        }

        // Save to History (Backend handles transfer and deletion of active session)
        // Use sessionId (real DB id) instead of examId
        // Guard: if sessionId not yet captured (race condition — user validates before debounced persist),
        // force a persist now to create the DB row and get the sessionId
        const examTypeValue = isVuExamen ? 'vu' : 'standard';
        let currentSessionId = sessionId;
        if (!currentSessionId) {
            const result = await examService.saveExamSession(examId, {
                moduleId, category, answers, currentQuestionIndex, mode, timeInSeconds,
                examType: examTypeValue
            });
            if (result?.sessionId) {
                currentSessionId = result.sessionId;
                setSessionId(currentSessionId);
            }
        }

        await examService.saveExamHistory(currentSessionId, {
            moduleId,
            category,
            score,
            timeInSeconds,
            answers,
            mode,
            examType: examTypeValue
        });

        return score;
    };

    const startReviewFromHistory = async (historyEntry, { passingScore = 75, passingSubScore = 0 } = {}) => {
        setIsLoading(true);
        setMinScore(passingScore);
        setMinSubScore(passingSubScore);

        try {
            // Create a review session ID based on history
            const id = `review-${historyEntry.id}-${Date.now()}`;
            const reviewExamType = historyEntry.isVuExamen ? 'vu' : 'standard';
            setIsVuExamen(!!historyEntry.isVuExamen);

            // Fetch questions for this exam
            const fetchedQuestions = await examService.getQuestionsByExam(null, null, historyEntry.examId, null, { type: reviewExamType });

            // Randomize options
            const processedQuestions = fetchedQuestions.map(q => ({
                ...q,
                options: q.options ? [...q.options].sort(() => Math.random() - 0.5) : []
            }));

            setQuestions(processedQuestions);
            setExamId(id);
            setModuleId(historyEntry.moduleId || historyEntry.examId); // Fallback if moduleId not in history
            setCategory(historyEntry.category);
            const cleanAnswers = sanitizeAnswers(historyEntry.answers);
            setAnswers(cleanAnswers);
            setCurrentQuestionIndex(0);
            setMode(historyEntry.mode || 'classic');
            setTimeInSeconds(historyEntry.duration || 0);
            setIsFinished(true); // Mark as finished since it's a review
            setHistoricalScore(historyEntry.score ?? null);
            setHistoricalDate(historyEntry.date ?? null);
            setOnDemandAnswers({});

            // Load all answers for review mode
            // Pass examType explicitly to avoid stale closure (isVuExamen not yet updated in state)
            const allAnswers = await loadAnswers(true, historyEntry.examId, reviewExamType);

            // Mark this exam as reviewed (for rewards badge tracking)
            try {
                await examService.markReviewed(historyEntry.id, historyEntry.isVuExamen ? 'vu' : undefined);
            } catch (e) {
                // Non-critical — badge tracking only
            }

            // Compute per-matière results for complet exams
            const cat = historyEntry.category;
            const userAnswers = cleanAnswers;
            if ((cat === 'complet' || cat === 'full') && allAnswers) {
                const matiereMap = {};
                processedQuestions.forEach((q) => {
                    if (!q.matiere) return;
                    const mId = q.matiere.id;
                    if (!matiereMap[mId]) {
                        matiereMap[mId] = { id: mId, code: q.matiere.code, name: q.matiere.name, total: 0, correct: 0 };
                    }
                    matiereMap[mId].total++;
                    const secret = allAnswers[q.id];
                    if (userAnswers[q.id] !== undefined && secret && String(userAnswers[q.id]) === String(secret.correctReponseId)) {
                        matiereMap[mId].correct++;
                    }
                });
                const computed = Object.values(matiereMap).map(m => ({
                    ...m,
                    score: m.total > 0 ? Math.round((m.correct / m.total) * 100) : 0,
                    passed: m.total > 0 ? Math.round((m.correct / m.total) * 100) >= passingSubScore : true
                }));
                setMatiereResults(computed);
            } else {
                setMatiereResults(null);
            }

        } catch (error) {
            console.error('Error starting review from history:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const startReviewFromCustomHistory = async (customEntry, { passingScore = 75, passingSubScore = 0 } = {}) => {
        setIsLoading(true);
        setMinScore(passingScore);
        setMinSubScore(passingSubScore);

        try {
            const id = `custom-review-${customEntry.id}-${Date.now()}`;

            // Fetch questions by their stored IDs (uses keyword-exams.php action=questions)
            const fetchedQuestions = await api.getQuestionsByIds(customEntry.questionIds);

            const processedQuestions = fetchedQuestions.map(q => ({
                ...q,
                options: q.options ? [...q.options].sort(() => Math.random() - 0.5) : []
            }));

            setQuestions(processedQuestions);
            setExamId(id);
            setModuleId(customEntry.levelVar);
            setCategory('custom');
            setIsKeywordExam(true); // Prevents autosave
            setCustomExamMeta(null); // Review, not a new exam
            const cleanAnswers = sanitizeAnswers(customEntry.answers);
            setAnswers(cleanAnswers);
            setCurrentQuestionIndex(0);
            setMode(customEntry.mode === 'QA' ? 'flash' : 'classic');
            setTimeInSeconds(customEntry.duration || 0);
            setIsFinished(true);
            setHistoricalScore(customEntry.score ?? null);
            setHistoricalDate(customEntry.date ?? null);
            setOnDemandAnswers({});

            // Load correct answers via keyword-exams.php action=answers
            const allAnswers = await api.getKeywordExamAnswers(customEntry.questionIds);
            setOnDemandAnswers(prev => ({ ...prev, ...allAnswers }));

            // Mark as reviewed
            try {
                await api.markCustomExamReviewed(customEntry.id);
            } catch (e) { /* non-critical */ }

        } catch (error) {
            console.error('Error starting custom review:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const value = {
        examId,
        moduleId,
        category,
        questions,
        onDemandAnswers,
        answers,
        currentQuestionIndex,
        setCurrentQuestionIndex,
        mode,
        setMode,
        isPaused,
        setIsPaused,
        isFinished,
        timeInSeconds,
        isLoading,
        minScore,
        minSubScore,
        matiereResults,
        hasAnnexe,
        annexeExamId,
        historicalScore,
        historicalDate,
        isVuExamen,
        isKeywordExam,
        examTitle,
        setExamTitle,
        startExam,
        startReviewFromHistory,
        startReviewFromCustomHistory,
        loadAnswers,
        pauseExam,
        resumeExam,
        stopExam,
        restartExam,
        submitAnswer,
        markQuestionSeen,
        finishExam
    };

    return <ExamContext.Provider value={value}>{children}</ExamContext.Provider>;
};

export const useExam = () => {
    const context = useContext(ExamContext);
    if (!context) throw new Error('useExam must be used within an ExamProvider');
    return context;
};
